import { useDataModel } from '@thd-nucleus/data-sources';
import { useEffect, useState } from 'react';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import castArray from 'lodash/castArray';
import _first from 'lodash/first';
import isEqual from 'lodash/isEqual';
import { getMappedSkus } from '../sagas/pricingSaga/_utils';
import toCamelCase from '../utils/camelCase';
import { thdApiMap } from '../api/graphQL/mapping/thdApiMapping';
import omsId from '../utils/getOmsIdFromUrl';
import isDraftProduct from '../utils/isDraftProduct';
import {
  LIFE_CYCLE_EVENT_CONFIGURATOR_ITEM_IDS,
  LIFE_CYCLE_EVENT_UPDATE_PRODUCT_INFO,
} from '../actions/hydration/constants';

const castProductPropertyToArray = (data) => {
  const product = castArray(data.products.product);
  return {
    products: {
      product,
    },
  };
};

const getSkus = (skuMap) => {
  let skus = [];
  skus = compact(skuMap.subSkus.map((sub) => sub.sku));
  if (isEmpty(skus)) {
    skus = [skuMap.sku];
  }
  return skus;
};

const useProductGraphData = ({
  skuMap, itemId, storeId, attributes, updateProductsGraphData
}) => {
  const [productData, setProductData] = useState({
    data: null,
    loading: false,
    error: null,
    itemIds: null,
  });

  let uniqueitemIds;
  let selectedItemIds = [];

  if (skuMap.skuType === 'HomeDepot_OMSID') {
    const skuNumbers = getMappedSkus(skuMap);
    uniqueitemIds = uniq(skuNumbers);
    selectedItemIds = uniq(getSkus(skuMap));

  } else {
    uniqueitemIds = [isDraftProduct ? omsId : itemId.toString()];
    selectedItemIds = uniqueitemIds;
  }

  let zipCode = '30339';
  if (
    typeof window !== 'undefined'
    && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails
  ) {
    zipCode = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails()?.zipcode;
  }
  let variables = { zipCode, storeId, itemIds: uniqueitemIds };
  let skipFetch = skuMap.isLoading || !skuMap?.isValid;

  const { data, loading, error } = useDataModel('products', {
    variables,
    ssr: false,
    skip: skipFetch,
  });

  if (!skipFetch && !isEqual(uniqueitemIds, productData.itemIds)) {
    setProductData({ loading, itemIds: uniqueitemIds });
  }

  let retItemIds = [];
  let mainItemId = omsId;
  if (data && data.products && !loading) {
    const productItemIds = data.products.map((product) => product.itemId);
    retItemIds = selectedItemIds.filter((id) => productItemIds.includes(id));
    if (productItemIds.includes(selectedItemIds[0])) {
      mainItemId = selectedItemIds[0];
    }
  }
  const retItemIdsStr = retItemIds.join(',');

  useEffect(() => {
    if (!loading) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(LIFE_CYCLE_EVENT_CONFIGURATOR_ITEM_IDS, { itemIds: retItemIds });
      window.LIFE_CYCLE_EVENT_BUS.trigger(LIFE_CYCLE_EVENT_UPDATE_PRODUCT_INFO, { itemId: mainItemId });
    }
  }, [mainItemId, retItemIdsStr, loading]);

  useEffect(() => {
    let currUniqueItemIds;
    if (skuMap.skuType === 'HomeDepot_OMSID') {
      const skuNumbers = getMappedSkus(skuMap);
      currUniqueItemIds = uniq(skuNumbers);
    } else {
      currUniqueItemIds = [isDraftProduct ? omsId : itemId.toString()];
    }

    if (
      data
      && !loading
      && ((!isEqual(data, productData.data) && isEqual(currUniqueItemIds, productData.itemIds))
        || skuMap.skuType === '')
    ) {
      const mappedData = thdApiMap(data);
      const castedData = castProductPropertyToArray(mappedData);
      let thdDetails = toCamelCase(castedData);
      let newSkus = {
        ...skuMap,
      };

      if (skuMap.skuType !== 'HomeDepot_OMSID') {
        newSkus.sku = isDraftProduct ? omsId : itemId.toString();
      }

      if (attributes?.allowDuplicateSubSkus) {
        const thdDetailsWithDuplicates = {
          products: {
            product: skuMap.subSkus.flatMap((s) => thdDetails.products.product.filter((p) => Number(s.sku) === p.defaultSku)
            ),
          },
        };
        thdDetails = thdDetailsWithDuplicates;
      }

      const updatedData = { newSkus, thdDetails };
      updateProductsGraphData(updatedData);
      setProductData({ data, loading: false, error, itemIds: currUniqueItemIds });
    }
  }, [data, loading, error, skuMap, attributes]);
};

export default useProductGraphData;
